import React, { useState } from 'react';

import { useDebouncedCallback } from 'use-debounce';

import { api } from '@api/reduxApi';
import { Input, Text, ZoomableImage } from '@components/common';
import { Label } from '@components/fields';
import { Models } from '@components/SurveyBuilder';
import { surveyBuilderApi } from '@components/SurveyBuilder/api';
import { getFormHelpers } from '@components/SurveyBuilder/hooks/useFormHelpers';
import { TrashSVG } from '@components/svgs';
import { Tooltip } from '@components/Tooltip';

import { MarkdownEditor } from '../../../common/MarkdownEditor';
import { FormExample } from '../FormExample';

export const BlockSettingsForm = ({ block: initialBlock }) => {
  const [block, setBlock] = useState<Models.Block>(initialBlock);
  const [updateImage] = api.useUpdateStudyScreenerFieldImageMutation();
  const [updateBlock] = surveyBuilderApi.useUpdateSurveyBuilderBlockMutation();

  const [title, setTitle] = React.useState<string>(block.title || '');
  const [description, setDescription] = React.useState<string>(block.description || '');

  const { callback: debouncedUpdateBlock } = useDebouncedCallback((data) => {
    updateBlock({ id: block.id, ...data });
  }, 500);

  const { getExample, getHelper, getPlaceholder } = getFormHelpers(block);

  const example = getExample();
  const helper = getHelper();
  const placeholder = getPlaceholder();

  const handleOnImageDelete = () => {
    if (block.screener_field_id) {
      const body = new FormData();
      body.append('screener_field[image]', 'remove');

      updateImage({ id: block.screener_field_id, body });

      setBlock({ ...block, image_url: null } as Models.Block);
    }
  };

  return (
    <>
      <fieldset className='mb-6'>
        <Label className='mb-1 text-base' labelFor='title'>
          Title
        </Label>

        {helper.title && (
          <Text h='400' color='gray-500' className='mb-2'>
            {helper.title}
          </Text>
        )}

        <Input
          id='title'
          className='w-full'
          name='title'
          placeholder={placeholder.title}
          onChange={(title) => {
            setTitle(title);
            debouncedUpdateBlock({ title });
          }}
          onBlur={(title) => {
            setTitle(title);
            updateBlock({ id: block.id, title } as Models.Block);
          }}
          value={title ?? ''}
        />

        {example.title && <FormExample className='mb-2 mt-4'>{example.title}</FormExample>}
      </fieldset>

      <fieldset className='mb-6'>
        <Label className='mb-1 text-base' labelFor='description'>
          Description
        </Label>

        {helper.description && (
          <Text h='400' color='gray-500' className='mb-2'>
            {helper.description}
          </Text>
        )}

        <MarkdownEditor
          textAreaProps={{
            id: 'description',
            name: 'description',
            placeholder: placeholder.description,
            minRows: 1,
            maxRows: 9,
            onChange: (e) => {
              setDescription(e.currentTarget.value);
              debouncedUpdateBlock({ description: e.currentTarget.value });
            },
            onBlur: (e) => {
              setDescription(e.currentTarget.value);
              updateBlock({ id: block.id, description: e.currentTarget.value } as Models.Block);
            },
            value: description ?? ''
          }}
        />

        {example.description && <FormExample className='mb-2 mt-4'>{example.description}</FormExample>}
      </fieldset>

      {block.image_url && (
        <fieldset>
          <Label className='mb-1 text-base'>Image</Label>

          <div className='relative mb-6 flex justify-center rounded-md border border-gray-200'>
            <ZoomableImage
              src={block.image_url}
              alt='Question attachment'
              className='h-48 w-96 object-contain object-center'
            />

            <div className='absolute right-2 top-2'>
              <Tooltip content='Delete image'>
                <button onClick={handleOnImageDelete} className='shadow-elevationSm rounded bg-white p-2'>
                  <TrashSVG />
                </button>
              </Tooltip>
            </div>
          </div>
        </fieldset>
      )}
    </>
  );
};
